import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../mock';

const CAROUSEL_INTERVAL_MS = 8000;

export const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const total = testimonials.length;

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % total);
  }, [total, setCurrent]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + total) % total);
  }, [total, setCurrent]);

  useEffect(() => {
    if (total <= 1) return;
    const id = setInterval(next, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [next, total]);

  const testimonial = testimonials[current];

  return (
    <section id="testimonials" className="py-10 px-4 bg-gradient-to-b from-white to-[#f0f9ff]">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a5f] mb-4">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real results from real people who trust Diamond Trucking School.
          </p>
        </div>

        <div className="relative">
          {total > 1 && (
            <button
              onClick={prev}
              data-testid="testimonial-prev"
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#d97706] hover:text-white transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          <Card className="shadow-xl border-t-4 border-[#d97706]" data-testid="testimonial-card">
            <CardContent className="pt-8 pb-8 px-8 md:px-12">
              <Quote className="w-12 h-12 text-[#d97706] mb-6 opacity-40" />

              <div className="flex gap-1 mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={`star-${i}`} className="w-5 h-5 fill-[#d97706] text-[#d97706]" />
                ))}
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-8 italic">
                "{testimonial.content}"
              </p>

              <div className="border-t pt-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-lg text-[#1e3a5f]">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {total > 1 && (
            <button
              onClick={next}
              data-testid="testimonial-next"
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#d97706] hover:text-white transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {total > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((t) => (
              <button
                key={`dot-${t.id}`}
                onClick={() => setCurrent(testimonials.indexOf(t))}
                data-testid={`testimonial-dot-${t.id}`}
                className={`w-3 h-3 rounded-full transition-all ${
                  testimonials.indexOf(t) === current ? 'bg-[#d97706] w-6' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
